import styled from 'styled-components';

import { StyleColor, NewStyleColor } from '../../../utils/styles-constants';
import { hexToRgba, spin } from '../../../utils/style';
import { ShowAloneSide } from '../../../containers/Editor/constants';
import constants from '../../../containers/Editor/utils/indesignEditor/constants';
import UserAgentUtils from '../../../utils/userAgent';

interface StyledPageProps {
	width: number;
	height: number;
	isTransparent: boolean;
	showAloneSide: ShowAloneSide;
	isSomeEditableItemActive: boolean;
	isAccessibleVersion: boolean;
}

interface PageInnerProps {
	scale: number;
}

interface StyledIframeProps {
	isLoading: boolean;
}

const StyledPage = styled('div')`
	position: relative;
	z-index: ${({ isSomeEditableItemActive }: StyledPageProps) => isSomeEditableItemActive ? 2 : 1};
	width: ${({ width }: StyledPageProps) => `${width}px`};
	height: ${({ height }: StyledPageProps) => `${height}px`};
	background-color: ${({ isTransparent, isAccessibleVersion }: StyledPageProps) => {
		if (isTransparent && isAccessibleVersion) {
			return StyleColor.Grey50;
		} else if (isTransparent) {
			return 'transparent';
		} else {
			return StyleColor.White;
		}
	}};
	${({ isAccessibleVersion }: StyledPageProps) => isAccessibleVersion ? 'overflow: auto;' : ''};

	${({ showAloneSide }: StyledPageProps) => {
		switch (showAloneSide) {
			case ShowAloneSide.LEFT:
				return 'margin-right: auto;';
			case ShowAloneSide.RIGHT:
				return 'margin-left: auto;';
			case ShowAloneSide.CENTER:
			default:
				return '';
		}
	}};

	${({ isSomeEditableItemActive, isAccessibleVersion }: StyledPageProps) => !isSomeEditableItemActive && (isAccessibleVersion ? `overflow: auto;` : `overflow: hidden;`)};

	/* this style is needed for proper line-height handling */
	[class*=${constants.textFieldClassName}] p span {
		vertical-align: bottom;
	}

	[class*=${constants.textFieldClassName}] {
		background: none !important; /* remove background images generated by inDesign (e.g., underline for hyperlink or bullets for list items) */
		cursor: text;

		&.edited {
			overflow: visible;
		}
	}

	.is-visible:not([class*=${constants.scrollableAreaClassName}]) {
		overflow: visible !important;

		> * {
			overflow: visible !important;
		}
	}

	[class*=${constants.imageClassName}] {
		overflow: visible !important;

		&.is-active {
			z-index: 2;
		}

		img {
			min-width: auto !important;
		}
	}

	/* we have to disable pointer-events on all elements for selecting editable text fields and images
	(for example, when there are buttons, links or layers above text fields and images) */
	> * {
		pointer-events: none;
	}

	[class*=${constants.scrollableAreaClassName}] > div {
		pointer-events: auto;

		a {
			pointer-events: none;
		}
	}

	._idGenButton {
		cursor: default;
	}

	._idGenButton[data-hidden],
	[class*=${constants.sliderClassName}][data-hidden] {
		display: block !important; /* to overwrite plugin's style and display editor */
	}

	.editable-button-link .edited[class*=${constants.textFieldClassName}],
	.editable-button-link .edited[class*=${constants.textFieldClassName}] * {
		pointer-events: auto;
	}

	[class*=${constants.lockedClassName}] {
		background-color: ${NewStyleColor.Grey300};
	}

	[class*=${constants.animationStartClassName}] {
		pointer-events: none !important;

		* {
			pointer-events: none !important;
		}
	}

	/* the plugin class is not first in the attribute any more, it arrives from the role after the custom one.
	   the trailing hyphen keeps the dynamic content slider out, it ends with the name instead of starting with it */
	[class*="${constants.sliderClassName}-"]:not([class*="${constants.editableSliderClassName}-"]) {
		background: ${NewStyleColor.Grey200} url('./.gallery/static/images/slider.svg');
		background-repeat: no-repeat;
		background-size: 30% auto;
		background-position: center;
	}
`;

const PageInner = styled('div')`
	transform: scale(${({ scale }: PageInnerProps) => scale});
	transform-origin: 0 0;
`;

const StyledIframe = styled('iframe')`
	flex: 1 1 auto;
	box-sizing: border-box;
	width: 100%;
	height: 100%;
	max-width: 100%;
	max-height: 100%;
	padding: 27px;
	border: 0;
	overflow: hidden;
	transition: opacity ease 1s;

	${({ isLoading }: StyledIframeProps) => isLoading && 'opacity: 0;'}
`;

const Loader = styled('div')`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	font-size: 21px;
	color: ${NewStyleColor.White};

	svg {
		height: 35px;
		width: 35px;
		animation: ${spin};
		margin-right: 20px;
	}
`;

// these styles are injected in iframe for styling editable components (RichTextEditor, MediaEditor, LinkEditor)
const htmlEditorStyles = `
	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.uprez-rte-wrapper {
		position: relative;
		overflow: hidden;
		pointer-events: auto;
	}

	.uprez-rte-wrapper.is-active {
		outline: 2px solid ${NewStyleColor.Blue100};
	}

	.uprez-rte-wrapper > div {
		height: 100%;
	}

	.uprez-rte-wrapper a {
		color: inherit;
		text-decoration: none;
	}

	.uprez-rte {
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 6px;
		scrollbar-width: thin;
		scrollbar-color: ${NewStyleColor.Grey500} ${NewStyleColor.Grey100};
	}

	.uprez-rte::-webkit-scrollbar {
		width: 5px;
		background-color: ${NewStyleColor.Grey100};
	}

	.uprez-rte::-webkit-scrollbar-track {
		background-color: ${NewStyleColor.Grey100};
		border-radius: 3px;
	}

	.uprez-rte::-webkit-scrollbar-thumb {
		background-color: ${NewStyleColor.Grey500};
		border-radius: 3px;
	}

	[data-uprez-image],
	[data-uprez-video] {
		position: relative;
		display: inline-block; /* to keep centered image/video in case parent element has text-align: center; property */
		vertical-align: middle;
	}

	[data-uprez-image] > img,
	[data-uprez-video] > img {
		width: 100%;
		vertical-align: bottom;
	}

	.uprez-media-frame {
		position: relative;
		overflow: hidden;
		pointer-events: auto;
		border-radius: inherit;
	}

	.uprez-media-frame.is-active {
		z-index: 100; /* to overlap other elements on the page when drag or resize image */
		overflow: visible;
	}

	.uprez-media-frame.is-active:not(.is-video-cover) img {
		width: 100%;
	}

	.uprez-media-frame.is-active.can-drag {
		pointer-events: none;
	}

	[class*="is-rounded"] .uprez-media-frame {
		border-radius: 50%;
	}

	.is-video-cover img,
	[data-uprez-image] svg,
	[data-uprez-video] svg {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		max-width: 100%;
		max-height: 100%;
		width: auto;
		height: auto;
		margin: auto;
		pointer-events: auto;
	}

	.is-default-video {
		overflow: hidden;
	}

	.is-default-video img {
		width: 100%;
		max-height: none;
		margin: 0;
	}

	[data-uprez-image] svg,
	[data-uprez-video] svg {
		width: 50%;
		height: 50%;
		fill: ${NewStyleColor.Grey400};
	}

	.uprez-media-frame-border {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1;
		border: 2px solid ${NewStyleColor.Grey400};
		border-radius: inherit;
	}

	.uprez-media-frame-border.is-active {
		border-color: ${NewStyleColor.Blue100};
	}

	.uprez-media-rnd {
		pointer-events: auto;
	}

	.uprez-media-rnd.is-active {
		z-index: -1;
	}

	.uprez-media-rnd.is-active img {
		outline: 2px solid ${NewStyleColor.Blue100};
	}

	.uprez-media-rnd.is-active .editor-media-controls div::before,
	.uprez-media-rnd.is-active .editor-media-controls div::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		-webkit-transform: translate(-50%, -50%);
		transform: translate(-50%, -50%);
	}

	.uprez-media-rnd.is-active .editor-media-controls div::before {
		width: 400%;
		height: 400%;
	}

	.uprez-media-rnd.is-active .editor-media-controls div::after {
		width: 100%;
		height: 100%;
		outline: 1px solid ${NewStyleColor.Blue100};
		background-color: ${NewStyleColor.White};
	}

	.uprez-media-frame-loader {
		position: absolute;
		top: 2px;
		bottom: 2px;
		left: 2px;
		right: 2px;
		z-index: 1;
	}

	.uprez-media-frame-mask {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		background-color: ${NewStyleColor.White};
		opacity: 0.9;
	}

	.uprez-media-frame-loader-inner {
		position: absolute;
		top: 50%;
		left: 50%;
		-webkit-transform: translate(-50%, -50%);
		transform: translate(-50%, -50%);
		padding: 5px;
		font-family: HelveticaNeue, sans-serif;
		font-size: 16px;
		line-height: 20px;
		color: ${NewStyleColor.Grey800};
		text-align: center;
	}

	.uprez-media-frame-loader-inner span {
		display: block;
	}

	.uprez-media-frame-spinner {
		position: relative;
		display: inline-block;
		width: 45px;
		height: 45px;
		color: ${StyleColor.Blue600};
		border-radius: 50%;
		animation: spin 2s linear infinite;
	}

	.uprez-media-frame-spinner::before,
	.uprez-media-frame-spinner::after {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		border-radius: 50%;
	}

	.uprez-media-frame-spinner::before {
		border: 4px solid currentColor;
		opacity: 0.2;
	}

	.uprez-media-frame-spinner::after {
		border: 4px solid currentColor;
		border-top-color: transparent;
	}

	.uprez-link-editor {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1;
		color: ${NewStyleColor.Grey400};
		border: 2px dashed currentColor;
		pointer-events: auto;
	}

	.uprez-link-editor.is-active {
		color: ${NewStyleColor.Blue100};
	}

	.uprez-link-editor.has-image {
		pointer-events: none;
	}

	.uprez-link-editor-icon {
		position: absolute;
		top: 0;
		right: 0;
		display: -webkit-box;
		display: -ms-flexbox;
		display: flex;
		-webkit-box-pack: center;
		-ms-flex-pack: center;
		justify-content: center;
		-webkit-box-align: center;
		-ms-flex-align: center;
		align-items: center;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		background-color: currentColor;
	}

	.uprez-link-editor-icon svg {
		width: 14px;
		height: 14px;
		fill: ${NewStyleColor.White};
	}

	.rte-underline .rte-font-color {
		text-decoration: underline !important;
	}

	body:not(.no-user-select) * {
		user-select: auto !important;
	}

	body.no-user-select {
		user-select: none;
	}

	html {
		background: ${NewStyleColor.White};
	}

	${!UserAgentUtils.isMobileApp
		? `
			body::-webkit-scrollbar {
				width: 16px;
			}

			body::-webkit-scrollbar-track,
			body::-webkit-scrollbar-corner {
				background-color: ${NewStyleColor.Grey900};
			}

			body::-webkit-scrollbar-thumb {
				background-color: ${hexToRgba(NewStyleColor.White, 20)};
			}
		`
		: ''
	}
`;

export {
	StyledPage,
	PageInner,
	StyledIframe,
	Loader,
	htmlEditorStyles,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Page/styles.ts